import React, { useEffect, useState } from 'react';
import { FiTrash2, FiPlus, FiSearch, FiRefreshCw } from 'react-icons/fi';
import {
  getAgencies,
  addAgency,
  removeAgency,
  getCredentialProfiles,
  runIvansJobAndWait,
  parseResultJson,
  type MonitoredAgency,
  type CredentialProfileSummary,
  type AgencySearchResult,
} from '../ivansMonitorService';

const AgenciesTab: React.FC = () => {
  const [agencies, setAgencies] = useState<MonitoredAgency[]>([]);
  const [profiles, setProfiles] = useState<CredentialProfileSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [nowcertsId, setNowcertsId] = useState('');
  const [credentialProfile, setCredentialProfile] = useState('');
  const [showNewLogin, setShowNewLogin] = useState(false);
  const [newLoginUsername, setNewLoginUsername] = useState('');
  const [newLoginPassword, setNewLoginPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Most setups monitor a single agency, so adding more (and the directory
  // search / manual entry that goes with it) stays tucked away until asked for.
  const [showAdd, setShowAdd] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Optional IVANS directory search, for finding an unfamiliar account
  // code by name/city — hidden entirely if there's no credential profile
  // yet to search with (needs some real login to query IVANS at all,
  // regardless of which agency you're trying to find).
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<AgencySearchResult[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([getAgencies(), getCredentialProfiles()]);
      setAgencies(a);
      setProfiles(p);
      if (!credentialProfile && p.length > 0) setCredentialProfile(p[0].name);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agencies.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Typing a login creates a new named profile (keyed by agency name);
  // leaving it blank reuses whichever existing profile is selected —
  // matches the original desktop app's Add Agency dialog behavior.
  const usingNewLogin = newLoginUsername.trim() !== '' || newLoginPassword.trim() !== '';

  // Shared by the manual form's own submit and the directory search's "Use"
  // button — takes the name/account explicitly rather than reading them off
  // state, since a directory pick needs to save immediately with the result
  // it was given, not wait a render cycle for setName/setAccountCode to
  // land before a second, separate click.
  const submitAgency = async (agencyName: string, agencyAccountCode: string) => {
    if (!agencyName.trim() || !agencyAccountCode.trim()) {
      setError('Agency name and account code are required.');
      return;
    }
    if (!usingNewLogin && !credentialProfile) {
      setError('Choose a credential profile, or type a new login.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await addAgency({
        name: agencyName.trim(),
        accountCode: agencyAccountCode.trim(),
        nowcertsAgencyDatabaseId: nowcertsId.trim() || undefined,
        credentialProfile: usingNewLogin ? undefined : credentialProfile,
        username: usingNewLogin ? newLoginUsername.trim() : undefined,
        password: usingNewLogin ? newLoginPassword.trim() : undefined,
      });
      setName('');
      setAccountCode('');
      setNowcertsId('');
      setNewLoginUsername('');
      setNewLoginPassword('');
      setShowAdd(false);
      setShowManual(false);
      setShowSearch(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add agency.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdd = () => submitAgency(name, accountCode);

  const handleRemove = async (agencyName: string) => {
    await removeAgency(agencyName);
    await load();
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchError(null);
    setSearchResults(null);
    try {
      const profileForSearch = credentialProfile || profiles[0]?.name;
      const final = await runIvansJobAndWait('ivans-search-agency', [
        JSON.stringify({ query: searchQuery.trim(), credentialProfile: profileForSearch }),
      ]);
      if (final.status !== 'completed') {
        setSearchError('Search failed — see log for detail.');
        return;
      }
      const parsed = parseResultJson<{ query: string; results: AgencySearchResult[] }>(final.log);
      setSearchResults(parsed?.results ?? []);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed unexpectedly.');
    } finally {
      setSearching(false);
    }
  };

  const handlePickResult = (result: AgencySearchResult) => {
    setName(result.name);
    setAccountCode(result.account);
    setSearchResults(null);
    setSearchQuery('');
  };

  // First setup (no agency yet): the manual form is the main way in and the
  // directory search stays tucked away. Once an agency exists, adding another
  // — and the search that goes with it — sits behind "Add additional IVANS mailbox".
  const noAgency = !loading && agencies.length === 0;
  const addOpen = showAdd || noAgency;
  const searchOpen = profiles.length > 0 && (agencies.length > 0 ? showAdd : showSearch);
  const manualOpen = noAgency || showManual || profiles.length === 0;

  return (
    <div className="ivm-section">
      <div className="ivm-section__header">
        <div className="ivm-section__title">{agencies.length > 1 ? 'Monitored agencies' : 'Monitored agency'}</div>
      </div>

      {loading ? (
        <div className="ca-empty">Loading…</div>
      ) : agencies.length === 0 ? (
        <div className="ca-empty">No agency yet — add yours below.</div>
      ) : (
        <div>
          {agencies.map((a) => (
            <div className="ivm-agency-row" key={a.name}>
              <div>
                <div className="ivm-agency-row__name">{a.name}</div>
                <div className="ivm-agency-row__meta">
                  Account {a.accountCode} · profile: {a.credentialProfile}
                  {a.nowcertsAgencyDatabaseId ? ` · NowCerts ID: ${a.nowcertsAgencyDatabaseId}` : ''}
                </div>
              </div>
              <button type="button" className="ivm-remove-btn" onClick={() => handleRemove(a.name)}>
                <FiTrash2 size={12} /> Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {!addOpen && (
        <button type="button" className="ivm-subtle-link" onClick={() => setShowAdd(true)}>
          + Add additional IVANS mailbox
        </button>
      )}

      {addOpen && (
        <>
      {searchOpen && (
        <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border-glass)' }}>
          <div className="ivm-section__title" style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>Find an Agency (IVANS Directory Search)</div>
          <div className="ca-run__grid">
            <label className="ca-run__field">
              <span>Search by name, account code, or city</span>
              <input
                className="ca-run__input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              />
            </label>
            <button type="button" className="ca-run__btn" disabled={searching} onClick={handleSearch}>
              {searching ? <FiRefreshCw size={13} className="spin" /> : <FiSearch size={13} />}
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>

          {searchError && <div className="ca-run__status ca-run__status--error">{searchError}</div>}

          {searchResults && searchResults.length === 0 && (
            <div className="ca-empty">No matches found.</div>
          )}

          {searchResults && searchResults.length > 0 && (
            <table className="ca-run__table">
              <thead>
                <tr><th>Account</th><th>Name</th><th>City</th><th>State</th><th></th></tr>
              </thead>
              <tbody>
                {searchResults.map((r) => (
                  <tr key={r.account}>
                    <td>{r.account}</td>
                    <td>{r.name}</td>
                    <td>{r.city}</td>
                    <td>{r.state}</td>
                    <td>
                      <button type="button" className="ca-run__btn" onClick={() => handlePickResult(r)}>Use</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}


          {noAgency && profiles.length > 0 && !showSearch && (
            <button type="button" className="ivm-subtle-link" onClick={() => setShowSearch(true)}>
              Find the account code in the IVANS directory
            </button>
          )}

          {!manualOpen && (
            <button type="button" className="ivm-subtle-link" onClick={() => setShowManual(true)}>
              Add manually
            </button>
          )}

          {manualOpen && (
      <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border-glass)' }}>
        <div className="ivm-section__title" style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>{noAgency ? 'Add your agency' : 'Enter details manually'}</div>
        <div className="ca-run__grid">
          <label className="ca-run__field">
            <span>Agency Name</span>
            <input className="ca-run__input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="ca-run__field">
            <span>IVANS Account Code</span>
            <input className="ca-run__input" value={accountCode} onChange={(e) => setAccountCode(e.target.value)} />
          </label>
          <label className="ca-run__field">
            <span>NowCerts Agency DB ID (optional)</span>
            <input className="ca-run__input" value={nowcertsId} onChange={(e) => setNowcertsId(e.target.value)} title="For your own reference only — not used to match or look up records." />
          </label>
          {!showNewLogin && profiles.length > 0 && (
            <label className="ca-run__field">
              <span>Credential Profile</span>
              <select className="ca-run__select" value={credentialProfile} onChange={(e) => setCredentialProfile(e.target.value)}>
                {profiles.map((p) => (
                  <option key={p.name} value={p.name}>{p.name}{p.isDefault ? ' (default)' : ''}</option>
                ))}
              </select>
            </label>
          )}

          {!showNewLogin ? (
            <button type="button" className="ivm-secondary-btn" onClick={() => setShowNewLogin(true)}>
              Use a different login
            </button>
          ) : (
            <>
              <label className="ca-run__field">
                <span>New login — Username</span>
                <input className="ca-run__input" value={newLoginUsername} onChange={(e) => setNewLoginUsername(e.target.value)} />
              </label>
              <label className="ca-run__field">
                <span>New login — Password</span>
                <input className="ca-run__input" type="password" value={newLoginPassword} onChange={(e) => setNewLoginPassword(e.target.value)} />
              </label>
              <button
                type="button"
                className="ivm-secondary-btn"
                onClick={() => { setShowNewLogin(false); setNewLoginUsername(''); setNewLoginPassword(''); }}
              >
                Cancel — use an existing login instead
              </button>
            </>
          )}

          <button type="button" className="ca-run__btn ca-run__btn--full" disabled={submitting} onClick={handleAdd}>
            <FiPlus size={13} /> Add Agency
          </button>
        </div>
      </div>

          )}

          {agencies.length > 0 && (
            <button type="button" className="ivm-subtle-link" onClick={() => { setShowAdd(false); setShowManual(false); }}>
              Cancel
            </button>
          )}
        </>
      )}

      {error && <div className="ca-run__status ca-run__status--error">{error}</div>}
    </div>
  );
};

export default AgenciesTab;
